<template>
  <div id="record" class="record-page">
    <CRow class="record-page__row content-fade-in" ref="container">
      <CCol class="record-page__col" xs="12" sm="12" md="10" lg="8" xl="7">
        <!-- Header gọn -->
        <div class="record-header">
          <div class="record-header__icon">
            <i class="fa fa-microphone-alt"></i>
          </div>
          <h1 class="record-header__title">Ghi âm trực tiếp</h1>
          <p class="record-header__subtitle">Thu âm từ microphone, nghe lại và biên tập</p>
        </div>

        <CCard class="record-card">
          <CCardBody class="record-card__body">
            <!-- Khu vực điều khiển ghi âm -->
            <div class="record-control">
              <div
                class="record-control__main"
                :class="{ 'record-control__main--recording': isRecording }">
                <button
                  v-if="!isRecording"
                  type="button"
                  class="record-btn record-btn--start btn-primary"
                  @click="startRecording"
                  aria-label="Bắt đầu ghi âm">
                  <span class="record-btn__icon"><i class="fa fa-microphone"></i></span>
                  <span class="record-btn__text">Bắt đầu ghi âm</span>
                </button>
                <template v-else>
                  <div class="record-recording">
                    <div class="record-recording__visual">
                      <span class="record-recording__dot"></span>
                      <span class="record-recording__bar"></span>
                      <span class="record-recording__bar"></span>
                      <span class="record-recording__bar"></span>
                      <span class="record-recording__bar"></span>
                      <span class="record-recording__bar"></span>
                    </div>
                    <div class="record-recording__time">{{ displayDuration }}</div>
                    <button
                      type="button"
                      class="record-btn record-btn--stop"
                      @click="stopRecording"
                      aria-label="Dừng ghi âm">
                      <i class="fa fa-stop"></i> Dừng
                    </button>
                  </div>
                </template>
              </div>
              <p v-if="recordError" class="record-error">
                <i class="fa fa-exclamation-circle"></i> {{ recordError }}
              </p>
            </div>

            <!-- Danh sách bản ghi -->
            <div class="record-section">
              <h3 class="record-section__title">
                <i class="fa fa-list-ul"></i> Bản ghi của bạn
              </h3>
              <div class="record-list">
                <transition-group name="record-list" tag="div" class="record-list__inner">
                  <div
                    v-for="(rec, idx) in recordList"
                    :key="rec.id"
                    class="record-item"
                    :class="{ 'record-item--selected': selectedRecord && selectedRecord.id === rec.id }"
                    @click="selectRecord(rec)">
                    <div class="record-item__icon">
                      <i class="fa fa-music"></i>
                    </div>
                    <div class="record-item__info">
                      <span class="record-item__label">Ghi âm {{ idx + 1 }}</span>
                      <span class="record-item__duration">{{ formatDurationHHMMSS(rec.durationSeconds) }}</span>
                    </div>
                    <button
                      v-if="selectedRecord && selectedRecord.id === rec.id"
                      type="button"
                      class="record-item__remove"
                      aria-label="Xóa bản ghi"
                      title="Xóa bản ghi"
                      @click.stop="removeRecord(rec)">
                      <i class="fa fa-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                </transition-group>
                <div v-if="recordList.length === 0" class="record-empty">
                  <i class="fa fa-folder-open"></i>
                  <p>Chưa có bản ghi nào. Nhấn nút phía trên để bắt đầu.</p>
                </div>
              </div>
            </div>

            <!-- Player nghe lại (custom progress để đồng bộ đúng với thời lượng) -->
            <transition name="fade-slide">
              <div v-if="selectedRecord && selectedRecord.url" class="record-player">
                <div class="record-player__label">
                  <i class="fa fa-play-circle"></i> Nghe lại
                </div>
                <audio
                  ref="playbackAudio"
                  :src="selectedRecord.url"
                  class="record-player__audio-hidden"
                  @loadedmetadata="onPlaybackLoadedMetadata"
                  @timeupdate="onPlaybackTimeUpdate"
                  @ended="onPlaybackEnded"/>
                <div class="record-player__controls">
                  <button
                    type="button"
                    class="record-player__play"
                    :aria-label="isPlaying ? 'Tạm dừng' : 'Phát'"
                    @click="togglePlayback">
                    <i :class="isPlaying ? 'fa fa-pause' : 'fa fa-play'"></i>
                  </button>
                  <span class="record-player__time record-player__time--current">{{ formatPlaybackTime(playbackCurrentTime) }}</span>
                  <input
                    type="range"
                    class="record-player__range"
                    min="0"
                    max="100"
                    step="0.1"
                    :value="playbackProgressPercent"
                    @input="onPlaybackSeek"
                    aria-label="Tiến độ phát"/>
                  <span class="record-player__time record-player__time--total">{{ formatPlaybackTime(playbackDuration) }}</span>
                </div>
              </div>
            </transition>

            <!-- Nút biên tập -->
            <transition name="fade-slide">
              <div v-if="isShowBtnFileAdd" class="record-actions">
                <button type="button" class="record-btn record-btn--primary" @click="clickAddFile()">
                  <i class="fa fa-edit"></i> Biên tập và lưu
                </button>
              </div>
            </transition>
          </CCardBody>
        </CCard>

      </CCol>
    </CRow>

    <!-- Modal biên tập -->
    <CModal
      :show.sync="isShowFileAdd"
      size="lg"
      :closeOnBackdrop="true"
      class="record-addfile-modal">
      <template v-slot:header>
        <div class="record-modal-header">
          <div class="record-modal-header__icon">
            <i class="fa fa-edit"></i>
          </div>
          <div class="record-modal-header__text">
            <h5 class="record-modal-header__title">Biên tập bản tin</h5>
            <span class="record-modal-header__subtitle">Nhập thông tin và lưu bản ghi âm</span>
          </div>
          <button type="button" class="record-modal-header__close" aria-label="Đóng" @click="isShowFileAdd = false">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </template>
      <div class="record-modal-body">
        <BaseAudioAddFile
          v-if="isShowFileAdd"
          :file="fileAddContent"
          :duration="durationFileAdd"
          @loading="loadingAdd($event)"
          :type="fileType"/>
      </div>
    </CModal>

    <BaseLoading :loading="loading.value"></BaseLoading>
  </div>
</template>

<script>
import BaseLoading from "../base/BaseFormLoading";
import BaseAudioAddFile from "@/views/base/BaseAddFile";
import { FileType } from "@/constant";

export default {
  name: "Record",
  components: { BaseLoading, BaseAudioAddFile },
  notifications: {
    showSuccessMsg: { type: "success", title: "Success" },
    showErrorMsg: { type: "error", title: "Errors" },
    showWarningMsg: { type: "warn", title: "warn" },
  },
  data() {
    return {
      fileType: FileType.GHI_AM_TEP_TIN,
      loading: { value: false },
      fileAddContent: {
        fileName: "",
        content: "",
      },
      durationFileAdd: "",
      isShowFileAdd: false,
      isShowBtnFileAdd: false,
      // MediaRecorder recording
      isRecording: false,
      mediaRecorder: null,
      stream: null,
      recordChunks: [],
      recordStartTime: 0,
      recordTimerId: null,
      recordDurationSeconds: 0,
      recordError: null,
      recordList: [],
      selectedRecord: null,
      // Custom player (đồng bộ progress với thời lượng thực)
      playbackCurrentTime: 0,
      playbackDuration: 0,
      isPlaying: false,
    };
  },
  computed: {
    displayDuration() {
      return this.formatDurationHHMMSS(this.recordDurationSeconds);
    },
    playbackProgressPercent() {
      if (!this.playbackDuration || this.playbackDuration <= 0) return 0;
      return Math.min(100, (this.playbackCurrentTime / this.playbackDuration) * 100);
    },
  },
  watch: {
    selectedRecord(rec) {
      if (rec) {
        this.playbackDuration = rec.durationSeconds || 0;
        this.playbackCurrentTime = 0;
        this.isPlaying = false;
      }
    },
  },
  methods: {
    async startRecording() {
      this.recordError = null;
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false,
          },
          video: false,
        });
      } catch (e) {
        this.recordError = "Không thể truy cập microphone. Vui lòng cho phép và thử lại.";
        this.showErrorMsg({ message: "Vui lòng cho phép truy cập micro và thử lại." });
        return;
      }
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const options = mimeType ? { mimeType } : {};
      this.mediaRecorder = new MediaRecorder(this.stream, options);
      this.recordChunks = [];
      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.recordChunks.push(e.data);
      };
      this.mediaRecorder.onstop = () => this.finishRecording();
      this.mediaRecorder.start(200);
      this.recordStartTime = Date.now();
      this.recordDurationSeconds = 0;
      this.isRecording = true;
      this.recordTimerId = setInterval(() => {
        this.recordDurationSeconds = (Date.now() - this.recordStartTime) / 1000;
      }, 100);
    },
    stopRecording() {
      if (!this.mediaRecorder || this.mediaRecorder.state === "inactive") return;
      if (this.recordTimerId) {
        clearInterval(this.recordTimerId);
        this.recordTimerId = null;
      }
      this.mediaRecorder.stop();
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
      this.mediaRecorder = null;
      this.isRecording = false;
    },
    finishRecording() {
      if (this.recordChunks.length === 0) return;
      const blob = new Blob(this.recordChunks, { type: "audio/webm" });
      const url = URL.createObjectURL(blob);
      const durationSeconds = this.recordDurationSeconds;
      const id = Date.now();
      const record = {
        id,
        blob,
        url,
        durationSeconds,
        durationFormatted: this.formatDurationHHMMSS(durationSeconds),
      };
      this.$helper.blobToBase64(blob).then((base64) => {
        record.base64 = base64;
      });
      this.recordList.push(record);
      this.recordChunks = [];
      this.selectedRecord = record;
      this.applySelectionToFileAdd();
    },
    formatDurationHHMMSS(seconds) {
      if (seconds == null || isNaN(seconds)) return "00:00:00";
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = Math.floor(seconds % 60);
      return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
    },
    selectRecord(rec) {
      if (this.selectedRecord && this.selectedRecord.id === rec.id) return;
      this.selectedRecord = rec;
      this.applySelectionToFileAdd();
    },
    applySelectionToFileAdd() {
      if (!this.selectedRecord) {
        this.isShowBtnFileAdd = false;
        return;
      }
      this.isShowBtnFileAdd = true;
      const rec = this.selectedRecord;
      const ext = (rec.blob && rec.blob.type.indexOf("webm") >= 0) ? "webm" : "mp3";
      this.fileAddContent.fileName = "Ghi_am_" + rec.id + "." + ext;
      if (rec.base64) {
        this.fileAddContent.content = rec.base64;
      } else {
        this.$helper.blobToBase64(rec.blob).then((base64) => {
          rec.base64 = base64;
          this.fileAddContent.content = base64;
        });
      }
      this.durationFileAdd = this.formatDurationHHMMSS(rec.durationSeconds);
    },
    removeRecord(rec) {
      if (rec.url) URL.revokeObjectURL(rec.url);
      const idx = this.recordList.findIndex((r) => r.id === rec.id);
      if (idx !== -1) this.recordList.splice(idx, 1);
      if (this.selectedRecord && this.selectedRecord.id === rec.id) {
        this.selectedRecord = this.recordList.length ? this.recordList[this.recordList.length - 1] : null;
        this.applySelectionToFileAdd();
      }
      if (this.recordList.length === 0) {
        this.isShowBtnFileAdd = false;
        this.isShowFileAdd = false;
      }
    },
    onPlaybackLoadedMetadata() {
      const el = this.$refs.playbackAudio;
      if (!el || !this.selectedRecord) return;
      const d = el.duration;
      if (this.selectedRecord.durationSeconds == null || this.selectedRecord.durationSeconds === 0) {
        this.selectedRecord.durationSeconds = d;
        this.selectedRecord.durationFormatted = this.formatDurationHHMMSS(d);
        this.applySelectionToFileAdd();
      }
      // Luôn dùng thời lượng đã ghi (record.durationSeconds) cho thanh progress để tránh lỗi duration WebM trên một số trình duyệt
      this.playbackDuration = this.selectedRecord.durationSeconds || (Number.isFinite(d) && d > 0 ? d : 0);
    },
    onPlaybackTimeUpdate() {
      const el = this.$refs.playbackAudio;
      if (el) this.playbackCurrentTime = el.currentTime;
    },
    onPlaybackEnded() {
      this.isPlaying = false;
      this.playbackCurrentTime = this.playbackDuration || 0;
    },
    togglePlayback() {
      const el = this.$refs.playbackAudio;
      if (!el) return;
      if (this.isPlaying) {
        el.pause();
      } else {
        el.play();
      }
      this.isPlaying = !this.isPlaying;
    },
    onPlaybackSeek(e) {
      const el = this.$refs.playbackAudio;
      if (!el || !this.playbackDuration) return;
      const pct = parseFloat(e.target.value);
      el.currentTime = (pct / 100) * this.playbackDuration;
      this.playbackCurrentTime = el.currentTime;
    },
    formatPlaybackTime(seconds) {
      if (seconds == null || !Number.isFinite(seconds)) return "0:00";
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return m + ":" + String(s).padStart(2, "0");
    },
    loadingAdd(value) {
      this.loading.value = !!value;
    },
    clickAddFile() {
      this.isShowFileAdd = true;
    },
  },
  beforeDestroy() {
    if (this.recordTimerId) clearInterval(this.recordTimerId);
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") this.mediaRecorder.stop();
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
    this.recordList.forEach((r) => r.url && URL.revokeObjectURL(r.url));
  },
};
</script>

<style lang="scss" scoped>
$primary: #0d6efd;
$primary-dark: #0a58ca;
$accent: #00b4d8;
$accent-light: #90e0ef;
$danger: #dc3545;
$danger-hover: #bb2d3b;
$success: #20c997;
$text: #2d3748;
$text-muted: #718096;
$bg-card: #ffffff;
$bg-soft: #f7fafc;
$border: #e2e8f0;
$radius: 16px;
$radius-sm: 10px;
$shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
$shadow-hover: 0 8px 30px rgba(0, 0, 0, 0.12);

.record-page {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 60vh;
  padding: 1.5rem 1rem 2rem;
}
.record-page__row {
  width: 100%;
  justify-content: center;
  margin: 0;
}
.record-page__col {
  margin-left: auto;
  margin-right: auto;
}

.record-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  &__icon {
    width: 40px;
    height: 40px;
    min-width: 40px;
    background: linear-gradient(135deg, $primary 0%, $accent 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
    i {
      font-size: 1.1rem;
      color: #fff;
    }
  }
  &__title {
    font-size: 1.25rem;
    font-weight: 700;
    color: $text;
    margin: 0;
    letter-spacing: -0.02em;
  }
  &__subtitle {
    font-size: 0.8rem;
    color: $text-muted;
    margin: 0;
    width: 100%;
    text-align: center;
    flex-basis: 100%;
  }
}

.record-card {
  border: none;
  border-radius: $radius;
  box-shadow: $shadow;
  overflow: hidden;
  transition: box-shadow 0.25s ease;
  &:hover {
    box-shadow: $shadow-hover;
  }
}
.record-card__body {
  padding: 2rem;
  @media (max-width: 576px) {
    padding: 1.5rem;
  }
}

.record-control {
  margin-bottom: 2rem;
}
.record-control__main {
  background: $bg-soft;
  border-radius: $radius;
  padding: 2rem;
  text-align: center;
  border: 2px dashed $border;
  transition: border-color 0.2s, background 0.2s;
  &--recording {
    border-style: solid;
    border-color: rgba($danger, 0.3);
    background: rgba($danger, 0.04);
  }
}
.record-btn {
  border: none;
  cursor: pointer;
  font-weight: 600;
  border-radius: $radius-sm;
  transition: transform 0.15s, box-shadow 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  &:active {
    transform: scale(0.98);
  }
  &--start {
    background: linear-gradient(135deg, $primary 0%, darken($primary, 6%) 100%);
    color: #fff;
    padding: 1rem 2rem;
    font-size: 1.05rem;
    box-shadow: 0 4px 14px rgba(13, 110, 253, 0.4);
    .record-btn__icon {
      width: 36px;
      height: 36px;
      background: rgba(255, 255, 255, 0.25);
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    &:hover {
      box-shadow: 0 6px 20px rgba(13, 110, 253, 0.5);
    }
  }
  &--stop {
    background: $danger;
    color: #fff;
    padding: 0.65rem 1.5rem;
    font-size: 0.95rem;
    margin-left: 1rem;
    &:hover {
      background: $danger-hover;
    }
  }
  &--primary {
    width: 100%;
    background: linear-gradient(135deg, $success 0%, darken($success, 8%) 100%);
    color: #fff;
    padding: 1rem 1.5rem;
    font-size: 1.05rem;
    margin-top: 1rem;
    box-shadow: 0 4px 14px rgba(32, 201, 151, 0.35);
    &:hover {
      box-shadow: 0 6px 20px rgba(32, 201, 151, 0.45);
    }
  }
}
.record-recording {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
}
.record-recording__visual {
  display: flex;
  align-items: center;
  gap: 4px;
}
.record-recording__dot {
  width: 10px;
  height: 10px;
  background: $danger;
  border-radius: 50%;
  animation: record-pulse 1.2s ease-in-out infinite;
}
.record-recording__bar {
  width: 4px;
  height: 24px;
  background: $accent;
  border-radius: 2px;
  animation: record-bars 0.8s ease-in-out infinite;
  &:nth-child(2) { animation-delay: 0.1s; }
  &:nth-child(3) { animation-delay: 0.2s; }
  &:nth-child(4) { animation-delay: 0.3s; }
  &:nth-child(5) { animation-delay: 0.4s; }
  &:nth-child(6) { animation-delay: 0.5s; }
  &:nth-child(7) { animation-delay: 0.6s; }
}
.record-recording__time {
  font-size: 1.5rem;
  font-weight: 700;
  color: $text;
  font-variant-numeric: tabular-nums;
  min-width: 90px;
}
.record-error {
  margin: 1rem 0 0;
  padding: 0.75rem 1rem;
  background: rgba($danger, 0.08);
  color: $danger;
  border-radius: $radius-sm;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@keyframes record-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.2); }
}
@keyframes record-bars {
  0%, 100% { transform: scaleY(0.5); }
  50% { transform: scaleY(1); }
}

.record-section__title {
  font-size: 1rem;
  font-weight: 600;
  color: $text;
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  i {
    color: $accent;
  }
}
.record-list {
  background: $bg-soft;
  border-radius: $radius-sm;
  padding: 0.75rem;
  max-height: 280px;
  overflow-y: auto;
}
.record-list__inner {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.record-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 1rem;
  border-radius: $radius-sm;
  cursor: pointer;
  background: #fff;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  position: relative;
  &:hover {
    background: #fff;
    border-color: $border;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
  &--selected {
    border-color: $primary;
    background: rgba($primary, 0.04);
    box-shadow: 0 2px 12px rgba(13, 110, 253, 0.15);
  }
  &__icon {
    width: 40px;
    height: 40px;
    min-width: 40px;
    background: linear-gradient(135deg, $accent-light 0%, $accent 100%);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 1rem;
  }
  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }
  &__label {
    font-weight: 500;
    color: $text;
  }
  &__duration {
    font-variant-numeric: tabular-nums;
    color: $text-muted;
    font-size: 0.9rem;
  }
  &__remove {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border: none;
    background: rgba($danger, 0.1);
    color: $danger;
    border-radius: $radius-sm;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s, color 0.2s;
    i {
      font-size: 1rem;
      display: inline-block;
      pointer-events: none;
    }
    &:hover {
      background: $danger;
      color: #fff;
    }
  }
}
.record-empty {
  text-align: center;
  padding: 2.5rem 1rem;
  color: $text-muted;
  i {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
    opacity: 0.5;
    display: block;
  }
  p {
    margin: 0;
    font-size: 0.9rem;
  }
}

.record-list-enter-active,
.record-list-leave-active {
  transition: all 0.3s ease;
}
.record-list-enter,
.record-list-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

.record-player {
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: $bg-soft;
  border-radius: $radius-sm;
  border: 1px solid $border;
}
.record-player__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: $text-muted;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.record-player__audio-hidden {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}
.record-player__controls {
  display: flex;
  align-items: center;
  gap: 0.5rem 0.75rem;
  flex-wrap: wrap;
}
.record-player__play {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border: none;
  border-radius: 50%;
  background: $primary;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, transform 0.1s;
  i {
    font-size: 1rem;
  }
  &:hover {
    background: $primary-dark;
  }
  &:active {
    transform: scale(0.96);
  }
}
.record-player__time {
  font-size: 0.9rem;
  font-variant-numeric: tabular-nums;
  color: $text-muted;
  min-width: 2.5rem;
  &--current {
    text-align: right;
  }
  &--total {
    color: $text;
  }
}
.record-player__range {
  flex: 1;
  min-width: 80px;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: $border;
  border-radius: 3px;
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: $primary;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: $primary;
    cursor: pointer;
    border: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
}

.record-actions {
  margin-top: 1rem;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Modal biên tập - animation */
.record-addfile-modal::v-deep .modal-dialog {
  transform-origin: center center;
  animation: record-modal-in 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.record-addfile-modal::v-deep .modal-content {
  border: none;
  border-radius: 16px;
  box-shadow: 0 24px 48px -12px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.record-addfile-modal::v-deep .modal-header {
  border: none;
  padding: 0;
  min-height: 0;
}
.record-addfile-modal::v-deep .modal-body {
  padding: 0;
}
.record-addfile-modal::v-deep .modal-footer {
  display: none;
}
@keyframes record-modal-in {
  0% {
    opacity: 0;
    transform: scale(0.92) translateY(-12px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
.record-modal-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 1rem 3rem 1rem 1.25rem;
}
.record-modal-header__icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  background: linear-gradient(135deg, $primary 0%, $accent 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  i {
    font-size: 1rem;
    color: #fff;
  }
}
.record-modal-header__text {
  flex: 1;
  min-width: 0;
}
.record-modal-header__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 2px;
}
.record-modal-header__subtitle {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
}
.record-modal-header__close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: #fff;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
  }
}
.record-modal-body {
  padding: 1.25rem;
}
</style>
